import { squaresFor } from '../grid/measure.js';
import { AREA_INCLUDES } from '../rules/system.js';
import { LEGACY_AREA } from '../legacy/records.js';
export const AREA_SHAPES = ['burst', 'cone', 'line', 'rect'];
/** Eight directions, clockwise from east: 0 E, 1 SE, 2 S, 3 SW, 4 W, 5 NW, 6 N, 7 NE. */
export const DIRECTIONS = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
export const AREA_LIMITS = { squares: 80, label: 60, areas: 40 };
const DIAGONALS = ['alternate', 'one', 'two', 'straight'];
export const AREA_KINDS = ['area.set', 'area.clear', 'area.clearAll'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isIndex = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 10_000;
const isSquares = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= AREA_LIMITS.squares;
/** An area stored before issue 1303 is in feet with no diagonal: read as what it was laid by (`legacy/records.ts`). */
const legacySquares = (v) => {
    const p = LEGACY_AREA.perSquare;
    return typeof v === 'number' && Number.isInteger(v) && v % p === 0 && isSquares(v / p) ? v / p : null;
};
export function parseArea(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const a = v;
    if (!isUuid(a.id) || !isUuid(a.ownerId) || !AREA_SHAPES.includes(a.shape))
        return null;
    const o = a.origin;
    if (!o || !isIndex(o.i) || !isIndex(o.j))
        return null;
    const legacy = a.diagonal === undefined;
    if (!legacy && !DIAGONALS.includes(a.diagonal))
        return null;
    if (a.includes !== undefined && a.includes !== null && !AREA_INCLUDES.includes(a.includes))
        return null;
    const read = (v) => (legacy ? legacySquares(v) : isSquares(v) ? v : null);
    const size = read(a.size);
    const size2 = read(a.size2 === undefined ? a.size : a.size2);
    if (size === null || size2 === null)
        return null;
    if (typeof a.dir !== 'number' || !Number.isInteger(a.dir) || a.dir < 0 || a.dir > 7)
        return null;
    if (a.label !== undefined && (typeof a.label !== 'string' || a.label.length > AREA_LIMITS.label))
        return null;
    if (a.floor !== undefined && !(typeof a.floor === 'string' && /^[a-z0-9-]{1,40}$/.test(a.floor)))
        return null;
    if (a.z !== undefined && !(typeof a.z === 'number' && Number.isFinite(a.z) && Math.abs(a.z) <= 20_000))
        return null;
    if (a.maker !== undefined && !isUuid(a.maker))
        return null;
    return {
        ...(typeof a.maker === 'string' ? { maker: a.maker } : {}),
        ...(typeof a.floor === 'string' ? { floor: a.floor } : {}), ...(typeof a.z === 'number' && a.z !== 0 ? { z: a.z } : {}),
        id: a.id, ownerId: a.ownerId, shape: a.shape, origin: { i: o.i, j: o.j }, size, size2,
        diagonal: legacy ? LEGACY_AREA.diagonal : a.diagonal,
        // An area from before issue 1303 was laid by 3.5's rules; one laid since with no rule caught no one by rule.
        includes: typeof a.includes === 'string' ? a.includes : legacy ? LEGACY_AREA.includes : null, dir: a.dir, label: typeof a.label === 'string' ? a.label.trim() : '',
    };
}
export function parseAreaBody(b) {
    switch (b.kind) {
        case 'area.set': {
            const area = parseArea(b.area);
            return area ? { kind: 'area.set', area } : null;
        }
        case 'area.clear': return isUuid(b.id) ? { kind: 'area.clear', id: b.id } : null;
        case 'area.clearAll': return { kind: 'area.clearAll' };
        default: return null;
    }
}
export function normaliseAreas(v) {
    const out = {};
    if (typeof v !== 'object' || v === null)
        return out;
    for (const a of Object.values(v)) {
        const p = parseArea(a);
        if (p)
            out[p.id] = p;
    }
    return out;
}
export function reduceAreas(areas, body) {
    switch (body.kind) {
        case 'area.set': return { ...areas, [body.area.id]: { ...body.area, origin: { ...body.area.origin } } };
        case 'area.clear': {
            if (!areas[body.id])
                return areas;
            const next = { ...areas };
            delete next[body.id];
            return next;
        }
        case 'area.clearAll': return {};
        default: return areas;
    }
}
/** Whether a person may clear an area: its owner, or the DM. */
export const mayClearArea = (a, viewer) => viewer.role === 'dm' || a.ownerId === viewer.id;
/**
 * The squares an area covers, as ROW SPANS (issue 840), clipped to the board (`cols` × `rows` squares), top row
 * first. A square counts the way the rules count it from an intersection: its FAR corner's distance, by the area's
 * diagonal rule, within the size — which is what gives a 3.5 20-ft burst its rules shape. Uncapped: an 80-square
 * burst is some 25,000 squares but at most 160 rows, so it is drawn whole, one rectangle a row.
 */
export function areaSpans(a, cols, rows) {
    const r = a.size;
    const out = [];
    const onBoard = (i, j) => i >= 0 && j >= 0 && i < cols && j < rows;
    // Add square (i, j), extending the row's last span when it is the next square along.
    const push = (i, j) => {
        if (!onBoard(i, j))
            return;
        const last = out[out.length - 1];
        if (last && last.j === j && last.i1 === i - 1)
            last.i1 = i;
        else
            out.push({ j, i0: i, i1: i });
    };
    // The far-corner distance from the origin intersection to square (i, j), in whole squares across and down.
    const reach = (i, j) => [i >= a.origin.i ? i - a.origin.i + 1 : a.origin.i - i, j >= a.origin.j ? j - a.origin.j + 1 : a.origin.j - j];
    if (a.shape === 'burst' || a.shape === 'cone') {
        const [ux, uy] = DIRECTIONS[a.dir];
        const len = Math.hypot(ux, uy);
        const iFrom = Math.max(0, a.origin.i - r);
        const iTo = Math.min(cols, a.origin.i + r);
        for (let j = Math.max(0, a.origin.j - r); j < Math.min(rows, a.origin.j + r); j += 1) {
            for (let i = iFrom; i < iTo; i += 1) {
                const [dx, dy] = reach(i, j);
                if (squaresFor(dx, dy, a.diagonal) > r)
                    continue;
                if (a.shape === 'cone') {
                    // Within 45° of its direction, measured to the square's centre: a quarter circle, as the rules' cone.
                    const cx = i + 0.5 - a.origin.i;
                    const cy = j + 0.5 - a.origin.j;
                    const cos = (cx * ux + cy * uy) / (Math.hypot(cx, cy) * len);
                    if (cos < Math.SQRT1_2 - 1e-9)
                        continue;
                }
                push(i, j);
            }
        }
        return out;
    }
    if (a.shape === 'line') {
        // From the origin along one of eight directions, one square wide: the squares it steps through, until its length is spent.
        const [ux, uy] = DIRECTIONS[a.dir];
        let i = ux >= 0 ? a.origin.i : a.origin.i - 1;
        let j = uy >= 0 ? a.origin.j : a.origin.j - 1;
        // An orthogonal line runs along a row of squares; which row is the one on the line's right-hand side.
        if (ux === 0)
            i = a.origin.i - (uy > 0 ? 1 : 0);
        if (uy === 0)
            j = a.origin.j - (ux > 0 ? 0 : 1);
        const cells = [];
        for (let k = 1;; k += 1) {
            const [dx, dy] = [Math.abs(ux) * k, Math.abs(uy) * k];
            if (squaresFor(dx, dy, a.diagonal) > r)
                break;
            if (onBoard(i, j))
                cells.push({ i, j });
            i += ux;
            j += uy;
            if (k > 400)
                break;
        }
        // Rows top first, each row's squares left to right (a westward line walks right to left).
        cells.sort((p, q) => p.j - q.j || p.i - q.i);
        for (const c of cells)
            push(c.i, c.j);
        return out;
    }
    // A rectangle: `size` across by `size2` down from the origin, growing toward its direction's quadrant.
    const w = r;
    const h = a.size2;
    const [ux, uy] = DIRECTIONS[a.dir];
    const i0 = ux < 0 ? a.origin.i - w : a.origin.i;
    const j0 = uy < 0 ? a.origin.j - h : a.origin.j;
    const lo = Math.max(0, i0);
    const hi = Math.min(cols - 1, i0 + w - 1);
    if (lo > hi)
        return out;
    for (let j = Math.max(0, j0); j < Math.min(rows, j0 + h); j += 1)
        out.push({ j, i0: lo, i1: hi });
    return out;
}
/** The same squares one by one, row by row (for counting and tests; the board draws `areaSpans`). */
export function areaCells(a, cols, rows) {
    const out = [];
    for (const s of areaSpans(a, cols, rows))
        for (let i = s.i0; i <= s.i1; i += 1)
            out.push({ i, j: s.j });
    return out;
}
//# sourceMappingURL=areas.js.map